import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { listComments, addComment, editComment, deleteComment } from '../functions/FetchComments';

const useStyles = makeStyles(() => ({
  comment: {
    borderBottom: '1px solid #e0e0e0',
    padding: '0.75rem 0',
    '&:last-child': { borderBottom: 'none' },
  },
  meta: {
    fontSize: '0.75rem',
    color: '#757575',
    marginTop: '0.25rem',
  },
  addBox: {
    marginTop: '1.25rem',
  },
  actions: {
    marginTop: '0.25rem',
  },
}));

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

export default function CommentsPanel({ module, recordKey, credentials, createSnack }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newText, setNewText] = useState('');
  const [editing, setEditing] = useState(null);

  const currentUser = (credentials.user.username || '').toLowerCase();
  const perms = credentials.user.roleActions?.comments || {};
  const isOwn = (c) => (c.createdBy || '').toLowerCase() === currentUser;

  const load = async () => {
    setLoading(true);
    try {
      const data = await listComments(module, recordKey, credentials);
      setComments(data || []);
    } catch {
      // lambdaFetch shows its own toast; show nothing extra to avoid doubling up
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    load();
  };

  const handleClose = () => {
    setOpen(false);
    setNewText('');
    setEditing(null);
  };

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSubmitting(true);
    try {
      const res = await addComment(module, recordKey, newText.trim(), credentials);
      if (res) {
        setNewText('');
        await load();
      }
    } catch {
      // lambdaFetch already surfaced the error toast
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing?.text?.trim()) return;
    setSubmitting(true);
    try {
      const res = await editComment(module, recordKey, editing.commentId, editing.text.trim(), credentials);
      if (res) {
        setEditing(null);
        await load();
      }
    } catch {
      // lambdaFetch already surfaced the error toast
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setSubmitting(true);
    try {
      const res = await deleteComment(module, recordKey, commentId, credentials);
      if (res) await load();
    } catch {
      // lambdaFetch already surfaced the error toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Comments">
        <IconButton size="small" onClick={handleOpen} style={{ marginLeft: '0.25rem' }}>
          <ChatBubbleOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Comments</DialogTitle>
        <DialogContent>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              {comments.length === 0 && !perms.create && (
                <Typography variant="body2" color="textSecondary">No comments yet.</Typography>
              )}

              {comments.map((c) => (
                <div key={c.commentId} className={classes.comment}>
                  {editing?.commentId === c.commentId ? (
                    <>
                      <TextField
                        value={editing.text}
                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        multiline
                        fullWidth
                        variant="outlined"
                        size="small"
                        disabled={submitting}
                        autoFocus
                      />
                      <div className={classes.actions}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={handleSaveEdit}
                          disabled={submitting || !editing.text.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditing(null)}
                          disabled={submitting}
                          style={{ marginLeft: '0.5rem' }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">{c.text}</Typography>
                      <div className={classes.meta}>
                        <span>{c.createdByName || c.createdBy}</span>
                        {' · '}
                        <span>{formatDate(c.createdAt)}</span>
                        {c.updatedAt !== c.createdAt && <span> (edited)</span>}
                      </div>
                      <div className={classes.actions}>
                        {perms.edit && isOwn(c) && (
                          <Button
                            size="small"
                            onClick={() => setEditing({ commentId: c.commentId, text: c.text })}
                            disabled={submitting}
                          >
                            Edit
                          </Button>
                        )}
                        {(isOwn(c) || perms.delete) && (
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => handleDelete(c.commentId)}
                            disabled={submitting}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}

              {perms.create && (
                <div className={classes.addBox}>
                  <TextField
                    label="Add a comment"
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={submitting}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAdd();
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleAdd}
                    disabled={!newText.trim() || submitting}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
